import { onBeforeUnmount, watch, type Ref } from 'vue'

// The window is the page's scroller on every screen (growth-ds ≥ 0.60 `LayoutScreen` leaves the scroll to the page —
// styles.md Gotchas), and an overlay alone does not stop it: a wheel or a drag over the overlay scrolls the page behind.
// Under 0.58 the screen scrolled inside itself and an overlay teleported to <body> had nothing to scroll. One lock for
// the app's overlays that sit over a visible page (the portal side menu, the DS `Modal` dialogs — the DS `Modal` has no
// lock of its own, the DS `Drawer` has): the first holder takes it, the last release gives back what it found.
//
// On <body>, not <html>: the DS `Drawer` locks <html> and restores what it found there, so the two locks never undo
// each other (<body>'s `overflow` reaches the viewport while <html>'s is `visible`). The vanished scrollbar's width
// becomes padding, as in the DS lock, so the page behind does not jump sideways.
let holders = 0
let restore: (() => void) | undefined

function lock() {
	holders += 1
	if (holders > 1) return
	const { body, documentElement: html } = document
	const { overflow, paddingRight } = body.style
	const scrollbar = window.innerWidth - html.clientWidth
	body.style.overflow = 'hidden'
	if (scrollbar > 0 && html.clientWidth > 0) body.style.paddingRight = `${scrollbar}px`
	restore = () => {
		body.style.overflow = overflow
		body.style.paddingRight = paddingRight
	}
}

function unlock() {
	if (holders === 0) return
	holders -= 1
	if (holders > 0) return
	restore?.()
	restore = undefined
}

/** Hold the page still while `active` is true, and give it back when it turns false or the component unmounts. */
export function usePageScrollLock(active: Ref<boolean>): void {
	let held = false
	const hold = (on: boolean) => {
		if (on === held) return
		held = on
		if (on) lock()
		else unlock()
	}
	watch(active, hold, { immediate: true })
	onBeforeUnmount(() => hold(false))
}
